import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../types';
import { PrintingState } from './printingSlice';
import { Workstation } from '../../../app/containers/OrderDetails/types';

const selectPrintingState = (state: RootState): PrintingState =>
  (state as any).printing || {
    currentQueue: null,
    isProcessing: false,
    completedJobs: [],
    failedJobs: [],
    progress: { completed: 0, total: 0, percentage: 0 },
    error: null,
    warnings: [],
    activeJobId: null,
    retryCounts: {},
    lastEvent: null,
  };

export const selectPrintQueue = createSelector(
  [selectPrintingState],
  (printing) => printing.currentQueue
);

export const selectIsProcessing = createSelector(
  [selectPrintingState],
  (printing) => printing.isProcessing
);

export const selectPrintProgress = createSelector(
  [selectPrintingState],
  (printing) => printing.progress
);

export const selectCompletedJobs = createSelector(
  [selectPrintingState],
  (printing) => printing.completedJobs
);

export const selectFailedJobs = createSelector(
  [selectPrintingState],
  (printing) => printing.failedJobs
);

export const selectPrintError = createSelector(
  [selectPrintingState],
  (printing) => printing.error
);

export const selectActivePrintJobId = createSelector(
  [selectPrintingState],
  (printing) => printing.activeJobId,
);

export const selectPrintRetryCounts = createSelector(
  [selectPrintingState],
  (printing) => printing.retryCounts,
);

export const selectPrintWarnings = createSelector(
  [selectPrintingState],
  (printing) => printing.warnings,
);

export const selectJobsByWorkstation = createSelector(
  [selectPrintQueue],
  (queue) => (workstation: Workstation) => {
    if (!queue) return [];
    return queue.jobs.filter(job => {
      if (job.workstation === workstation) return true;
      return Array.isArray(job.workstations) && job.workstations.includes(workstation);
    });
  }
);

export const selectJobsByType = createSelector(
  [selectPrintQueue],
  (queue) => (type: string) => {
    if (!queue) return [];
    return queue.jobs.filter(job => job.type === type);
  }
);

export const selectTotalPages = createSelector(
  [selectPrintQueue],
  (queue) => queue?.totalPages || 0
);

export const selectGroupingStrategy = createSelector(
  [selectPrintQueue],
  (queue) => queue?.groupingStrategy || 'document-type'
);
